"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import {
  Terminal,
  Download,
  Server,
  Play,
  CheckCircle2,
  Cpu,
  MemoryStick,
  HardDrive,
  Monitor,
  Apple,
  Laptop,
  Box,
  ChevronRight,
  Zap,
  Shield,
  Clock,
  ArrowRight,
  RotateCcw,
  Power,
  Settings,
  Folder,
  Database,
  Pause,
  SkipForward,
  Gauge,
  Activity,
  Wifi,
  RefreshCw,
  PlayCircle,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"

// Template data matching the actual script
const TEMPLATES = [
  { id: 1, name: "macOS Monterey", size: "14.5", type: "macos", icon: Apple, color: "from-gray-500 to-gray-600" },
  {
    id: 2,
    name: "macOS Ventura",
    size: "14.8",
    type: "macos",
    icon: Apple,
    recommended: true,
    color: "from-purple-500 to-purple-600",
  },
  { id: 3, name: "macOS Sonoma", size: "15.2", type: "macos", icon: Apple, color: "from-pink-500 to-pink-600" },
  { id: 4, name: "macOS Sequoia", size: "15.6", type: "macos", icon: Apple, color: "from-emerald-500 to-emerald-600" },
  {
    id: 5,
    name: "macOS Tahoe",
    size: "16.1",
    type: "macos",
    icon: Apple,
    experimental: true,
    color: "from-blue-500 to-blue-600",
  },
  { id: 6, name: "Windows 10", size: "5.2", type: "windows", icon: Monitor, color: "from-blue-500 to-blue-600" },
  {
    id: 7,
    name: "Windows 11",
    size: "5.8",
    type: "windows",
    icon: Monitor,
    recommended: true,
    color: "from-cyan-500 to-cyan-600",
  },
  { id: 8, name: "Server 2022", size: "6.1", type: "windows", icon: Server, color: "from-sky-500 to-sky-600" },
  { id: 9, name: "Server 2025", size: "6.4", type: "windows", icon: Server, color: "from-indigo-500 to-indigo-600" },
  { id: 10, name: "Tiny 10", size: "2.8", type: "windows", icon: Laptop, color: "from-violet-500 to-violet-600" },
  { id: 11, name: "Tiny 11", size: "3.1", type: "windows", icon: Laptop, color: "from-fuchsia-500 to-fuchsia-600" },
  { id: 12, name: "HSVE DSM", size: "1.9", type: "linux", icon: Database, color: "from-teal-500 to-teal-600" },
  { id: 13, name: "HSVE Android", size: "3.4", type: "linux", icon: Box, color: "from-green-500 to-green-600" },
  { id: 14, name: "HSVE Neon", size: "3.1", type: "linux", icon: Box, color: "from-cyan-500 to-cyan-600" },
  { id: 15, name: "HSVE Flex", size: "2.4", type: "linux", icon: Box, color: "from-orange-500 to-orange-600" },
  { id: 16, name: "Debian 13.4", size: "1.2", type: "linux", icon: Box, color: "from-rose-500 to-rose-600" },
]

// Simulated terminal output for script execution
const TERMINAL_LINES = [
  { text: 'root@pve:~# bash -c "$(curl -fsSL https://hsve.cc/run)"', delay: 0, type: "command" },
  { text: "", delay: 200, type: "blank" },
  { text: "  ██╗  ██╗███████╗██╗   ██╗███████╗", delay: 80, type: "ascii" },
  { text: "  ██║  ██║██╔════╝██║   ██║██╔════╝", delay: 40, type: "ascii" },
  { text: "  ███████║███████╗██║   ██║█████╗  ", delay: 40, type: "ascii" },
  { text: "  ██╔══██║╚════██║╚██╗ ██╔╝██╔══╝  ", delay: 40, type: "ascii" },
  { text: "  ██║  ██║███████║ ╚████╔╝ ███████╗", delay: 40, type: "ascii" },
  { text: "  ╚═╝  ╚═╝╚══════╝  ╚═══╝  ╚══════╝", delay: 40, type: "ascii" },
  { text: "", delay: 80, type: "blank" },
  { text: "  HSVE Proxmox Template Deployment Script v2.5.0", delay: 150, type: "info" },
  { text: "  Copyright © 2025 HSVE - All Rights Reserved", delay: 100, type: "info" },
  { text: "", delay: 80, type: "blank" },
  { text: "[INFO] Validating license key...", delay: 300, type: "process" },
  { text: "[✓] License: HSVE-DEMO-XXXX-XXXX-XXXX", delay: 400, type: "success" },
  { text: "[✓] Licensed to: demo@hsve.cc", delay: 200, type: "success" },
  { text: "[✓] License expires: 2025-12-31", delay: 200, type: "success" },
  { text: "", delay: 80, type: "blank" },
  { text: "[INFO] Detecting Proxmox environment...", delay: 300, type: "process" },
  { text: "[✓] Proxmox VE 8.2.4 detected", delay: 350, type: "success" },
  { text: "[✓] CPU: AMD EPYC 7763 (128 cores)", delay: 200, type: "success" },
  { text: "[✓] RAM: 512 GB DDR4 ECC", delay: 200, type: "success" },
  { text: "[✓] Storage: local-zfs (4.8 TB available)", delay: 200, type: "success" },
  { text: "[✓] Network: vmbr0 (10Gbps bonded)", delay: 200, type: "success" },
  { text: "", delay: 80, type: "blank" },
  { text: "╔══════════════════════════════════════════════════════════╗", delay: 100, type: "divider" },
  { text: "║              AVAILABLE TEMPLATES                        ║", delay: 50, type: "header" },
  { text: "╠══════════════════════════════════════════════════════════╣", delay: 50, type: "divider" },
]

type Stage = "intro" | "terminal" | "selection" | "download" | "proxmox" | "configure" | "booting" | "complete"

export default function DeployDemoPage() {
  const [stage, setStage] = useState<Stage>("intro")
  const [terminalLines, setTerminalLines] = useState<string[]>([])
  const [currentLineIndex, setCurrentLineIndex] = useState(0)
  const [selectedTemplate, setSelectedTemplate] = useState<(typeof TEMPLATES)[0] | null>(null)
  const [downloadProgress, setDownloadProgress] = useState(0)
  const [downloadSpeed, setDownloadSpeed] = useState(0)
  const [downloadedSize, setDownloadedSize] = useState(0)
  const [peakSpeed, setPeakSpeed] = useState(0)
  const [vmConfig, setVmConfig] = useState({ ram: 8, cores: 4, storage: 64, vmid: 100 })
  const [isBooting, setIsBooting] = useState(false)
  const [bootProgress, setBootProgress] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [networkStats, setNetworkStats] = useState<number[]>(Array(60).fill(0))
  const [selectedStorage, setSelectedStorage] = useState("local-zfs")
  const terminalRef = useRef<HTMLDivElement>(null)
  const downloadIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Terminal animation effect
  useEffect(() => {
    if (stage === "terminal" && currentLineIndex < TERMINAL_LINES.length && !isPaused) {
      const line = TERMINAL_LINES[currentLineIndex]
      const timeout = setTimeout(() => {
        setTerminalLines((prev) => [...prev, line.text])
        setCurrentLineIndex((prev) => prev + 1)

        // Auto-scroll terminal
        if (terminalRef.current) {
          terminalRef.current.scrollTop = terminalRef.current.scrollHeight
        }
      }, line.delay)

      return () => clearTimeout(timeout)
    } else if (stage === "terminal" && currentLineIndex >= TERMINAL_LINES.length) {
      setTimeout(() => setStage("selection"), 300)
    }
  }, [stage, currentLineIndex, isPaused])

  // Download simulation effect with realistic 2Gbps speeds
  useEffect(() => {
    if (stage === "download" && selectedTemplate && !isPaused) {
      const totalSize = Number.parseFloat(selectedTemplate.size) * 1024 // Convert GB to MB

      downloadIntervalRef.current = setInterval(() => {
        setDownloadProgress((prev) => {
          if (prev >= 100) {
            if (downloadIntervalRef.current) clearInterval(downloadIntervalRef.current)
            setTimeout(() => setStage("proxmox"), 800)
            return 100
          }

          // Simulate realistic 2Gbps speed with variations (200-260 MB/s)
          const baseSpeed = 230
          const variation = (Math.random() - 0.5) * 60
          const currentSpeed = Math.max(180, Math.min(260, baseSpeed + variation))

          setDownloadSpeed(currentSpeed)
          setPeakSpeed((p) => Math.max(p, currentSpeed))

          // Update network stats visualization
          setNetworkStats((prev) => {
            const newStats = [...prev.slice(1), (currentSpeed / 260) * 100]
            return newStats
          })

          const increment = (currentSpeed / totalSize) * 100 * 0.05
          const newProgress = Math.min(prev + increment, 100)
          setDownloadedSize((newProgress / 100) * totalSize)

          return newProgress
        })
      }, 50)

      return () => {
        if (downloadIntervalRef.current) clearInterval(downloadIntervalRef.current)
      }
    }
  }, [stage, selectedTemplate, isPaused])

  // Boot animation
  useEffect(() => {
    if (stage === "booting") {
      const interval = setInterval(() => {
        setBootProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval)
            setTimeout(() => setStage("complete"), 500)
            return 100
          }
          return prev + 2
        })
      }, 100)
      return () => clearInterval(interval)
    }
  }, [stage])

  const startDemo = () => {
    setStage("terminal")
    setTerminalLines([])
    setCurrentLineIndex(0)
  }

  const selectTemplate = (template: (typeof TEMPLATES)[0]) => {
    setSelectedTemplate(template)
    setDownloadProgress(0)
    setDownloadedSize(0)
    setPeakSpeed(0)
    setNetworkStats(Array(60).fill(0))
    setStage("download")
  }

  const skipToNextStage = () => {
    if (stage === "terminal") {
      setTerminalLines(TERMINAL_LINES.map((l) => l.text))
      setStage("selection")
    } else if (stage === "download") {
      if (downloadIntervalRef.current) clearInterval(downloadIntervalRef.current)
      setDownloadProgress(100)
      setStage("proxmox")
    }
  }

  const startConfiguration = () => {
    setStage("configure")
  }

  const bootVM = () => {
    setBootProgress(0)
    setStage("booting")
  }

  const resetDemo = () => {
    if (downloadIntervalRef.current) clearInterval(downloadIntervalRef.current)
    setStage("intro")
    setTerminalLines([])
    setCurrentLineIndex(0)
    setSelectedTemplate(null)
    setDownloadProgress(0)
    setDownloadedSize(0)
    setPeakSpeed(0)
    setVmConfig({ ram: 8, cores: 4, storage: 64, vmid: 100 })
    setIsBooting(false)
    setBootProgress(0)
    setIsPaused(false)
    setNetworkStats(Array(60).fill(0))
  }

  const getLineColor = (text: string, index: number) => {
    const lineType = TERMINAL_LINES[index]?.type
    switch (lineType) {
      case "command":
        return "text-emerald-400"
      case "ascii":
        return "text-[#e57312]"
      case "success":
        return "text-emerald-400"
      case "process":
        return "text-cyan-400"
      case "info":
        return "text-neutral-400"
      case "header":
        return "text-white font-bold"
      case "divider":
        return "text-neutral-600"
      default:
        return "text-neutral-300"
    }
  }

  const stages: { id: Stage; label: string; icon: React.ElementType }[] = [
    { id: "terminal", label: "Script Execution", icon: Terminal },
    { id: "selection", label: "Template Selection", icon: Folder },
    { id: "download", label: "High-Speed Download", icon: Download },
    { id: "proxmox", label: "Proxmox Interface", icon: Server },
    { id: "configure", label: "VM Configuration", icon: Settings },
    { id: "complete", label: "VM Ready", icon: CheckCircle2 },
  ]

  const stageOrder: Stage[] = [
    "intro",
    "terminal",
    "selection",
    "download",
    "proxmox",
    "configure",
    "booting",
    "complete",
  ]
  const currentStageIndex = stageOrder.indexOf(stage)

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0a] via-[#111] to-[#0a0a0a]">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#e57312]/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Header */}
      <div className="relative border-b border-neutral-800/50 bg-black/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/" className="text-[#e57312] font-bold text-xl hover:text-[#ff8c3a] transition-colors">
              HSVE
            </Link>
            <span className="text-neutral-700">/</span>
            <span className="text-neutral-400">Interactive Deployment Demo</span>
          </div>
          <div className="flex items-center gap-3">
            {stage !== "intro" && (
              <>
                {(stage === "terminal" || stage === "download") && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsPaused(!isPaused)}
                      className="border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-600 bg-transparent"
                    >
                      {isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
                      {isPaused ? "Resume" : "Pause"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={skipToNextStage}
                      className="border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-600 bg-transparent"
                    >
                      <SkipForward className="w-4 h-4 mr-2" />
                      Skip
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetDemo}
                  className="border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-600 bg-transparent"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </>
            )}
            <Link href="/templates/purchase">
              <Button size="sm" className="bg-[#e57312] hover:bg-[#c96510] text-white shadow-lg shadow-[#e57312]/20">
                Get Full Access
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      {stage !== "intro" && (
        <div className="relative border-b border-neutral-800/50 bg-neutral-950/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-1 text-sm overflow-x-auto pb-1">
              {stages.map((step, index) => {
                const stepIndex = stageOrder.indexOf(step.id)
                const isActive = stepIndex <= currentStageIndex
                const isCurrent = step.id === stage || (stage === "booting" && step.id === "configure")

                return (
                  <div key={step.id} className="flex items-center">
                    <div
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                        isCurrent
                          ? "bg-[#e57312]/20 text-[#e57312] shadow-lg shadow-[#e57312]/10"
                          : isActive
                            ? "text-emerald-400"
                            : "text-neutral-600"
                      }`}
                    >
                      {isActive && !isCurrent ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        <step.icon className={`w-4 h-4 ${isCurrent ? "animate-pulse" : ""}`} />
                      )}
                      <span className="whitespace-nowrap font-medium">{step.label}</span>
                    </div>
                    {index < stages.length - 1 && (
                      <ChevronRight
                        className={`w-4 h-4 mx-1 flex-shrink-0 ${isActive ? "text-neutral-500" : "text-neutral-800"}`}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 py-8">
        {/* Intro Stage */}
        {stage === "intro" && (
          <div className="flex flex-col items-center justify-center min-h-[70vh] text-center">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-[#e57312]/30 rounded-3xl blur-2xl animate-pulse" />
              <div className="relative w-24 h-24 rounded-2xl bg-gradient-to-br from-[#e57312] to-[#c96510] flex items-center justify-center shadow-2xl shadow-[#e57312]/30">
                <Terminal className="w-12 h-12 text-white" />
              </div>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Experience{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#e57312] to-[#ff9d4d]">HSVE</span>{" "}
              Deployment
            </h1>
            <p className="text-xl text-neutral-400 max-w-2xl mb-8 leading-relaxed">
              Watch our Proxmox template script in action — from license validation to VM boot. This interactive demo
              simulates the complete deployment workflow at 2Gbps speeds.
            </p>

            <div className="flex flex-wrap gap-6 justify-center mb-12">
              {[
                { icon: Zap, text: "2Gbps Download", color: "text-[#e57312]" },
                { icon: Shield, text: "License Validated", color: "text-emerald-400" },
                { icon: Clock, text: "Minutes to Deploy", color: "text-blue-400" },
                { icon: Server, text: "Proxmox Interface", color: "text-purple-400" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-900/50 border border-neutral-800"
                >
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <span className="text-neutral-300">{item.text}</span>
                </div>
              ))}
            </div>

            <Button
              size="lg"
              onClick={startDemo}
              className="bg-gradient-to-r from-[#e57312] to-[#c96510] hover:from-[#c96510] hover:to-[#e57312] text-white text-lg px-10 py-7 rounded-xl shadow-2xl shadow-[#e57312]/30 transition-all duration-300 hover:scale-105 group"
            >
              <PlayCircle className="w-6 h-6 mr-3 group-hover:animate-pulse" />
              Start Interactive Demo
            </Button>

            <p className="mt-6 text-sm text-neutral-500">No signup required • Fully interactive • Takes ~2 minutes</p>
          </div>
        )}

        {/* Terminal Stage */}
        {(stage === "terminal" || stage === "selection") && (
          <div className="space-y-6 animate-fadeIn">
            {/* Terminal Window */}
            <div className="rounded-xl border border-neutral-800 overflow-hidden bg-neutral-950 shadow-2xl shadow-black/50">
              {/* Terminal Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800">
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/50" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-lg shadow-yellow-500/50" />
                    <div className="w-3 h-3 rounded-full bg-green-500 shadow-lg shadow-green-500/50" />
                  </div>
                  <span className="text-neutral-400 text-sm font-mono">root@pve:~</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <Activity className="w-3 h-3 text-emerald-400 animate-pulse" />
                  <span>Connected</span>
                </div>
              </div>

              {/* Terminal Content */}
              <div
                ref={terminalRef}
                className="p-4 font-mono text-sm h-[450px] overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-700 scrollbar-track-transparent"
              >
                {terminalLines.map((line, index) => (
                  <div
                    key={index}
                    className={`${getLineColor(line, index)} leading-relaxed transition-all duration-150`}
                  >
                    {line || "\u00A0"}
                  </div>
                ))}

                {/* Template Selection UI */}
                {stage === "selection" && (
                  <div className="mt-4 space-y-3 animate-fadeIn">
                    {TEMPLATES.map((template) => (
                      <div key={template.id} className="flex items-center text-neutral-400">
                        <span className="text-[#e57312] w-8">{template.id.toString().padStart(2, "0")})</span>
                        <span className="text-white">{template.name}</span>
                        <span className="ml-2 text-neutral-600">({template.size} GB)</span>
                        {template.recommended && <span className="ml-2 text-emerald-400">[Recommended]</span>}
                        {template.experimental && <span className="ml-2 text-purple-400">[Experimental]</span>}
                      </div>
                    ))}
                    <div className="mt-4 text-cyan-400">
                      Enter template number (1-15): <span className="animate-pulse">█</span>
                    </div>
                  </div>
                )}

                {/* Blinking cursor during terminal stage */}
                {stage === "terminal" && !isPaused && (
                  <span className="inline-block w-2 h-4 bg-emerald-400 animate-pulse ml-1" />
                )}
                {stage === "terminal" && isPaused && <span className="inline-block w-2 h-4 bg-yellow-400 ml-1" />}
              </div>
            </div>

            {/* Template Selection Cards */}
            {stage === "selection" && (
              <div className="animate-fadeIn">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Folder className="w-5 h-5 text-[#e57312]" />
                  Select a Template to Download
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => selectTemplate(template)}
                      className="flex items-center justify-between p-4 rounded-xl border border-neutral-800 hover:border-[#e57312]/50 hover:bg-neutral-900/50 transition-all duration-300 group text-left hover:shadow-lg hover:shadow-[#e57312]/10"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg bg-gradient-to-br ${template.color} flex items-center justify-center shadow-lg`}
                        >
                          <template.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium group-hover:text-[#e57312] transition-colors">
                              {template.name}
                            </span>
                            {template.recommended && (
                              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                                Recommended
                              </span>
                            )}
                            {template.experimental && (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                                Experimental
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-neutral-500">{template.size} GB</span>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-neutral-600 group-hover:text-[#e57312] group-hover:translate-x-1 transition-all" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Download Stage */}
        {stage === "download" && selectedTemplate && (
          <div className="space-y-6 animate-fadeIn">
            <div className="rounded-xl border border-neutral-800 overflow-hidden bg-neutral-950 shadow-2xl">
              {/* Terminal Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800">
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                  </div>
                  <span className="text-neutral-400 text-sm font-mono">Downloading {selectedTemplate.name}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Wifi className="w-3 h-3 text-emerald-400 animate-pulse" />
                  <span className="text-emerald-400">2Gbps Connection Active</span>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Download Header */}
                <div className="flex items-center gap-4">
                  <div
                    className={`w-16 h-16 rounded-xl bg-gradient-to-br ${selectedTemplate.color} flex items-center justify-center shadow-lg`}
                  >
                    <selectedTemplate.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">{selectedTemplate.name}</h3>
                    <p className="text-neutral-400 text-sm font-mono">
                      vzdump-qemu-hsve{selectedTemplate.name.toLowerCase().replace(/\s+/g, "")}.vma.zst
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-[#e57312] font-mono">{downloadProgress.toFixed(1)}%</div>
                    <div className="text-sm text-neutral-500">Complete</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="h-4 bg-neutral-800 rounded-full overflow-hidden relative">
                    <div
                      className="h-full bg-gradient-to-r from-[#e57312] via-[#ff9d4d] to-[#e57312] rounded-full transition-all duration-100 relative overflow-hidden"
                      style={{ width: `${downloadProgress}%` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-neutral-500">
                    <span>cdn.hsve.cc</span>
                    <span>
                      {(downloadedSize / 1024).toFixed(2)} GB / {selectedTemplate.size} GB
                    </span>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800 text-center">
                    <Gauge className="w-6 h-6 text-[#e57312] mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white font-mono">{downloadSpeed.toFixed(0)}</div>
                    <div className="text-xs text-neutral-500">MB/s Current</div>
                  </div>
                  <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800 text-center">
                    <Zap className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white font-mono">{peakSpeed.toFixed(0)}</div>
                    <div className="text-xs text-neutral-500">MB/s Peak</div>
                  </div>
                  <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800 text-center">
                    <Download className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white font-mono">{(downloadedSize / 1024).toFixed(2)}</div>
                    <div className="text-xs text-neutral-500">GB Downloaded</div>
                  </div>
                  <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800 text-center">
                    <Clock className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white font-mono">
                      {downloadProgress >= 100
                        ? "Done"
                        : `${Math.max(0, Math.ceil((Number.parseFloat(selectedTemplate.size) * 1024 - downloadedSize) / downloadSpeed))}s`}
                    </div>
                    <div className="text-xs text-neutral-500">Remaining</div>
                  </div>
                </div>

                {/* Network Activity Visualization */}
                <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-neutral-300 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#e57312]" />
                      Network Throughput
                    </span>
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                      Live • 2Gbps
                    </span>
                  </div>
                  <div className="h-20 flex items-end gap-px rounded-lg overflow-hidden bg-neutral-950 p-2">
                    {networkStats.map((value, i) => (
                      <div
                        key={i}
                        className="flex-1 bg-gradient-to-t from-[#e57312] to-[#ff9d4d] rounded-t transition-all duration-75"
                        style={{ height: `${Math.max(2, value)}%`, opacity: 0.5 + (i / networkStats.length) * 0.5 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Proxmox Interface Stage */}
        {stage === "proxmox" && selectedTemplate && (
          <div className="space-y-6 animate-fadeIn">
            {/* Proxmox-style Window */}
            <div className="rounded-xl border border-neutral-700 overflow-hidden shadow-2xl">
              {/* Proxmox Header */}
              <div className="bg-gradient-to-r from-[#2a4a6b] to-[#1e3a5f] px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Server className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">Proxmox VE - pve</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <span>root@pam</span>
                  <span className="px-2 py-0.5 bg-white/10 rounded text-xs">Admin</span>
                </div>
              </div>

              <div className="flex">
                {/* Sidebar */}
                <div className="w-64 bg-neutral-900 border-r border-neutral-700 p-4 space-y-4">
                  <div className="text-sm font-medium text-neutral-400 uppercase tracking-wider">Server View</div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 p-2 rounded bg-[#2a4a6b]/30 text-white">
                      <Server className="w-4 h-4" />
                      <span>Datacenter</span>
                    </div>
                    <div className="ml-4 space-y-1">
                      <div className="flex items-center gap-2 p-2 rounded bg-neutral-800 text-[#e57312] border border-[#e57312]/30">
                        <Server className="w-4 h-4" />
                        <span>pve</span>
                      </div>
                      <div className="ml-4 space-y-1">
                        <div className="flex items-center gap-2 p-2 rounded hover:bg-neutral-800 text-neutral-400 cursor-pointer">
                          <HardDrive className="w-4 h-4" />
                          <span>local-zfs</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 rounded hover:bg-neutral-800 text-neutral-400 cursor-pointer">
                          <Database className="w-4 h-4" />
                          <span>local</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 bg-neutral-950 p-6">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-white">Restore VM from Backup</h2>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          Template Downloaded
                        </span>
                      </div>
                    </div>

                    {/* Form */}
                    <div className="grid gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm text-neutral-400">Source Storage</label>
                          <select
                            value={selectedStorage}
                            onChange={(e) => setSelectedStorage(e.target.value)}
                            className="w-full p-3 rounded-lg bg-neutral-900 border border-neutral-700 text-white focus:border-[#e57312] focus:outline-none"
                          >
                            <option value="local-zfs">local-zfs (4.8 TB available)</option>
                            <option value="local">local (256 GB available)</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm text-neutral-400">Backup File</label>
                          <div className="p-3 rounded-lg bg-neutral-900 border border-neutral-700 text-white font-mono text-sm">
                            vzdump-qemu-hsve{selectedTemplate.name.toLowerCase().replace(/\s+/g, "")}.vma.zst
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm text-neutral-400">VM ID</label>
                          <input
                            type="number"
                            value={vmConfig.vmid}
                            onChange={(e) =>
                              setVmConfig((prev) => ({ ...prev, vmid: Number.parseInt(e.target.value) || 100 }))
                            }
                            className="w-full p-3 rounded-lg bg-neutral-900 border border-neutral-700 text-white focus:border-[#e57312] focus:outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm text-neutral-400">Target Storage</label>
                          <select className="w-full p-3 rounded-lg bg-neutral-900 border border-neutral-700 text-white focus:border-[#e57312] focus:outline-none">
                            <option>local-zfs</option>
                            <option>local</option>
                          </select>
                        </div>
                      </div>

                      {/* Options */}
                      <div className="p-4 rounded-lg bg-neutral-900/50 border border-neutral-800 space-y-3">
                        <div className="text-sm font-medium text-white">Restore Options</div>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
                            <input
                              type="checkbox"
                              defaultChecked
                              className="rounded bg-neutral-800 border-neutral-600 text-[#e57312] focus:ring-[#e57312]"
                            />
                            Start after restore
                          </label>
                          <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
                            <input
                              type="checkbox"
                              className="rounded bg-neutral-800 border-neutral-600 text-[#e57312] focus:ring-[#e57312]"
                            />
                            Unique MAC addresses
                          </label>
                          <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
                            <input
                              type="checkbox"
                              defaultChecked
                              className="rounded bg-neutral-800 border-neutral-600 text-[#e57312] focus:ring-[#e57312]"
                            />
                            Overwrite existing
                          </label>
                          <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
                            <input
                              type="checkbox"
                              className="rounded bg-neutral-800 border-neutral-600 text-[#e57312] focus:ring-[#e57312]"
                            />
                            Bandwidth limit
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <Button
                        variant="outline"
                        className="border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-600 bg-transparent"
                      >
                        Cancel
                      </Button>
                      <Button onClick={startConfiguration} className="bg-[#2a4a6b] hover:bg-[#3a5a7b] text-white">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Restore
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configuration Stage */}
        {(stage === "configure" || stage === "booting") && selectedTemplate && (
          <div className="space-y-6 animate-fadeIn">
            {/* Proxmox-style Window */}
            <div className="rounded-xl border border-neutral-700 overflow-hidden shadow-2xl">
              {/* Proxmox Header */}
              <div className="bg-gradient-to-r from-[#2a4a6b] to-[#1e3a5f] px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Server className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">
                    VM {vmConfig.vmid} ({selectedTemplate.name})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {stage === "booting" ? (
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm flex items-center gap-1">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Starting...
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-neutral-500/20 text-neutral-400 rounded-full text-sm flex items-center gap-1">
                      <Power className="w-4 h-4" />
                      Stopped
                    </span>
                  )}
                </div>
              </div>

              <div className="flex">
                {/* Sidebar */}
                <div className="w-48 bg-neutral-900 border-r border-neutral-700 p-2 space-y-1">
                  {[
                    "Summary",
                    "Console",
                    "Hardware",
                    "Cloud-Init",
                    "Options",
                    "Task History",
                    "Backup",
                    "Snapshots",
                  ].map((item, i) => (
                    <div
                      key={item}
                      className={`p-2 rounded text-sm cursor-pointer transition-colors ${
                        i === 2
                          ? "bg-[#2a4a6b]/30 text-white"
                          : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                      }`}
                    >
                      {item}
                    </div>
                  ))}
                </div>

                {/* Main Content */}
                <div className="flex-1 bg-neutral-950 p-6">
                  {stage === "booting" ? (
                    <div className="flex flex-col items-center justify-center h-64 space-y-6">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full border-4 border-neutral-800 border-t-[#e57312] animate-spin" />
                        <Power className="w-8 h-8 text-[#e57312] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-semibold text-white mb-2">Starting Virtual Machine</div>
                        <div className="text-neutral-400">Booting {selectedTemplate.name}...</div>
                      </div>
                      <div className="w-64">
                        <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#e57312] rounded-full transition-all duration-300"
                            style={{ width: `${bootProgress}%` }}
                          />
                        </div>
                        <div className="text-center text-sm text-neutral-500 mt-2">{bootProgress}%</div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-white">Hardware Configuration</h2>
                        <Button onClick={bootVM} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          <Power className="w-4 h-4 mr-2" />
                          Start VM
                        </Button>
                      </div>

                      {/* Resource Sliders */}
                      <div className="grid gap-6">
                        <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Cpu className="w-5 h-5 text-blue-400" />
                              <span className="text-white font-medium">CPU Cores</span>
                            </div>
                            <span className="text-2xl font-bold text-blue-400">{vmConfig.cores}</span>
                          </div>
                          <Slider
                            value={[vmConfig.cores]}
                            onValueChange={([value]) => setVmConfig((prev) => ({ ...prev, cores: value }))}
                            min={1}
                            max={32}
                            step={1}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-neutral-500">
                            <span>1 core</span>
                            <span>32 cores</span>
                          </div>
                        </div>

                        <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MemoryStick className="w-5 h-5 text-purple-400" />
                              <span className="text-white font-medium">Memory (RAM)</span>
                            </div>
                            <span className="text-2xl font-bold text-purple-400">{vmConfig.ram} GB</span>
                          </div>
                          <Slider
                            value={[vmConfig.ram]}
                            onValueChange={([value]) => setVmConfig((prev) => ({ ...prev, ram: value }))}
                            min={2}
                            max={64}
                            step={2}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-neutral-500">
                            <span>2 GB</span>
                            <span>64 GB</span>
                          </div>
                        </div>

                        <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <HardDrive className="w-5 h-5 text-emerald-400" />
                              <span className="text-white font-medium">Storage</span>
                            </div>
                            <span className="text-2xl font-bold text-emerald-400">{vmConfig.storage} GB</span>
                          </div>
                          <Slider
                            value={[vmConfig.storage]}
                            onValueChange={([value]) => setVmConfig((prev) => ({ ...prev, storage: value }))}
                            min={32}
                            max={512}
                            step={32}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-neutral-500">
                            <span>32 GB</span>
                            <span>512 GB</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Complete Stage */}
        {stage === "complete" && selectedTemplate && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-fadeIn">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-emerald-500/30 rounded-full blur-2xl animate-pulse" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30">
                <CheckCircle2 className="w-12 h-12 text-white" />
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              VM Successfully <span className="text-emerald-400">Deployed!</span>
            </h1>
            <p className="text-xl text-neutral-400 max-w-2xl mb-8">
              Your {selectedTemplate.name} virtual machine is now running and ready to use.
            </p>

            {/* VM Summary Card */}
            <div className="w-full max-w-md p-6 rounded-xl bg-neutral-900/50 border border-neutral-800 text-left mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-12 h-12 rounded-lg bg-gradient-to-br ${selectedTemplate.color} flex items-center justify-center`}
                >
                  <selectedTemplate.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{selectedTemplate.name}</h3>
                  <span className="text-sm text-emerald-400 flex items-center gap-1">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    Running
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-400">{vmConfig.cores}</div>
                  <div className="text-xs text-neutral-500">CPU Cores</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-400">{vmConfig.ram} GB</div>
                  <div className="text-xs text-neutral-500">Memory</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-400">{vmConfig.storage} GB</div>
                  <div className="text-xs text-neutral-500">Storage</div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={resetDemo}
                variant="outline"
                className="border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-600 bg-transparent"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Another Template
              </Button>
              <Link href="/templates/purchase">
                <Button className="bg-[#e57312] hover:bg-[#c96510] text-white shadow-lg shadow-[#e57312]/20">
                  Get Full Access
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  )
}
